/**
 * The record shapes the panel reads back from `/admin`.
 *
 * `packages/shared` owns the request bodies and the fleet row; what a store or a
 * screen looks like on the way out is not pinned there, so it is pinned here
 * instead, against the stored entities the API converts from.
 *
 * One deliberate asymmetry: a timezone is validated strictly when the panel
 * SENDS it — `storeUpsertSchema` refuses a zone this runtime does not know — but
 * read back as a plain string. Refusing to render a whole store list because one
 * browser's ICU build has not heard of a zone would be the wrong trade; the form
 * still shows the value and still refuses to save a bad one.
 */

import { z } from 'zod';
import {
  fleetScreenSchema,
  openingHoursEntrySchema,
  rotationDegreesSchema,
  sessionUserSchema,
  updateWindowSchema,
} from '@pbtv/shared';
import { collectionOf, entityOf, withIdAlias } from './envelope.js';

export const storeRecordSchema = withIdAlias(
  z.object({
    storeId: z.string().min(1),
    name: z.string(),
    brand: z.string(),
    timezone: z.string().min(1),
    address: z.string().default(''),
    openingHours: z.array(openingHoursEntrySchema).default([]),
    /**
     * Nullish rather than required: the deploy order is shared, then API, then panel, so
     * a panel that knows this field will genuinely be shown store rows from an API that
     * does not yet send it. Absent must read as "no window", which holds a windowed
     * command back rather than releasing it at an unknown hour.
     */
    updateWindow: updateWindowSchema.nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    /** Present when the route counts them for us; the fleet list covers it otherwise. */
    screenCount: z.number().int().nonnegative().nullish(),
  }),
  'storeId',
);

export type StoreRecord = z.infer<typeof storeRecordSchema>;

export const storeListSchema = collectionOf(storeRecordSchema, 'stores');
export const storeEnvelopeSchema = entityOf(storeRecordSchema, 'store');

/**
 * One screen, read from what `GET /admin/screens/:screenId` actually sends: a
 * fleet row — whose identity, name and rotation live in a nested `screen`
 * object — plus the fields the fleet list leaves out, of which `sizeInches` is
 * the one worth the extra request.
 *
 * It is flattened on the way in, so the rest of the panel works with a plain
 * record and this one wire-shape quirk stays in a single place. Everything past
 * the identity is heartbeat-owned and absent until a device has checked in.
 */
export const screenRecordSchema = fleetScreenSchema
  .extend({
    sizeInches: z.number().positive().nullish(),
    pairedAt: z.string().nullish(),
  })
  .transform((detail) => ({
    screenId: detail.screen.id,
    storeId: detail.storeId,
    name: detail.screen.name,
    rotationDegrees: detail.screen.rotationDegrees,
    autoStart: detail.screen.autoStart,
    // Not from `screen`: this one is a server-side setting, not a manifest field.
    alwaysOn: detail.alwaysOn,
    sizeInches: detail.sizeInches ?? null,
    pairedAt: detail.pairedAt ?? null,
    lastHeartbeatAt: detail.lastHeartbeatAt,
  }));

export type ScreenRecord = z.infer<typeof screenRecordSchema>;

// `detail` is the envelope the route uses; `screen` is accepted too so an older
// deployment answering the previous name still parses.
export const screenEnvelopeSchema = entityOf(screenRecordSchema, 'detail', 'screen');
export const fleetListSchema = collectionOf(fleetScreenSchema, 'screens');

export const profileSchema = entityOf(sessionUserSchema, 'user', 'profile');
